import { useState, useEffect } from 'react';
import { shippingSenderService } from '../../../services/shippingSenderService';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ShippingSender, CreateShippingSenderRequest, UpdateShippingSenderRequest } from '../../../types/shippingSender';

interface UseShippingSendersParams {
  companyId: number | null;
}

export function useShippingSenders({ companyId }: UseShippingSendersParams) {
  const [senders, setSenders] = useState<ShippingSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Tracks which row's deactivate button is in flight — not a single shared flag — so
  // clicking one profile's "remove" doesn't disable every other row's button too.
  const [deactivatingSenderId, setDeactivatingSenderId] = useState<number | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const fetchSenders = async () => {
      setLoading(true);
      setError(null);
      try {
        const loaded = await shippingSenderService.listSenders(companyId);
        if (!cancelled) {
          setSenders(loaded);
        }
      } catch (err) {
        console.error('Failed to load shipping senders:', err);
        if (!cancelled) {
          setError('Не вдалося завантажити профілі відправників');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSenders();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const createSender = async (request: CreateShippingSenderRequest): Promise<string | null> => {
    if (!companyId) return 'Ідентифікатор компанії відсутній';

    setSaving(true);
    setSaveError(null);
    try {
      const created = await shippingSenderService.createSender(companyId, request);
      setSenders((prev) => [...prev, created]);
      return null;
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Не вдалося створити профіль відправника');
      setSaveError(message);
      return message;
    } finally {
      setSaving(false);
    }
  };

  const updateSender = async (senderId: number, request: UpdateShippingSenderRequest): Promise<string | null> => {
    if (!companyId) return 'Ідентифікатор компанії відсутній';

    setSaving(true);
    setSaveError(null);
    try {
      const updated = await shippingSenderService.updateSender(companyId, senderId, request);
      setSenders((prev) => prev.map((sender) => (sender.id === senderId ? updated : sender)));
      return null;
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Не вдалося оновити профіль відправника');
      setSaveError(message);
      return message;
    } finally {
      setSaving(false);
    }
  };

  const deactivateSender = async (senderId: number): Promise<string | null> => {
    if (!companyId) return 'Ідентифікатор компанії відсутній';

    setDeactivatingSenderId(senderId);
    setDeactivateError(null);
    try {
      await shippingSenderService.deactivateSender(companyId, senderId);
      setSenders((prev) => prev.filter((sender) => sender.id !== senderId));
      return null;
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Не вдалося прибрати профіль відправника');
      setDeactivateError(message);
      return message;
    } finally {
      setDeactivatingSenderId(null);
    }
  };

  return {
    data: {
      senders,
      loading,
      error,
    },
    save: {
      saving,
      error: saveError,
      create: createSender,
      update: updateSender,
    },
    deactivate: {
      submittingSenderId: deactivatingSenderId,
      error: deactivateError,
      run: deactivateSender,
    },
  };
}
